import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Tickets.css';
import './Orders.css';

const VAT_DIVISOR = 1.25;
const DEFAULT_QUOTE_PROFIT_MARGIN = '15';
const DEFAULT_QUOTE_CATEGORY = 'Hardware';

function formatQuoteAmount(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

function getLinePriceIncVat(line) {
  if (!line) return null;
  if (line.priceIncVat != null && line.priceIncVat !== '') {
    const n = Number(line.priceIncVat);
    return Number.isFinite(n) ? n : null;
  }
  if (line.unitPrice !== '' && line.unitPrice != null && line.unitPrice !== undefined) {
    const n = Number(line.unitPrice);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Ex-VAT line amount: order-total price is not multiplied by qty. */
function lineExVatAmount(line) {
  const inc = getLinePriceIncVat(line);
  if (inc == null || !Number.isFinite(inc)) return null;
  if (line.priceIsOrderTotal) {
    return inc / VAT_DIVISOR;
  }
  const qty = Math.max(0, Number(line.quantity));
  const q = qty > 0 && Number.isFinite(qty) ? qty : 1;
  return (inc / VAT_DIVISOR) * q;
}

/** Same rounding as server economicClient.roundMoney */
function roundQuoteMoney(n) {
  if (n == null || Number.isNaN(n)) return 0;
  return Math.round(Number(n) * 100) / 100;
}

/** Ex-VAT revenue when profit is margin % of that revenue: cost ÷ (1 − margin/100). */
function exVatSaleFromMarginOnSalePreview(costExVat, marginPct) {
  if (costExVat == null || !Number.isFinite(costExVat)) return null;
  const m = Number.isFinite(marginPct) ? Math.max(0, marginPct) : 0;
  if (m >= 100) return null;
  const d = 1 - m / 100;
  if (d <= 0) return null;
  return roundQuoteMoney(costExVat / d);
}

const Orders = () => {
  const { selectedTenant, authenticatedFetch, hasPrivilege } = useAuth();
  const [orders, setOrders] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [searchFilters, setSearchFilters] = useState({});

  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteOrder, setQuoteOrder] = useState(null);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [profitMargin, setProfitMargin] = useState(DEFAULT_QUOTE_PROFIT_MARGIN);
  const [cbcLines, setCbcLines] = useState([]);
  const [productDescriptions, setProductDescriptions] = useState([]);
  const [lineCategories, setLineCategories] = useState({});
  const [quoteModalLoading, setQuoteModalLoading] = useState(false);
  const [quoteModalError, setQuoteModalError] = useState(null);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSubmitError, setQuoteSubmitError] = useState(null);
  const [quoteSubmitSuccess, setQuoteSubmitSuccess] = useState(null);

  const acceptOfferBusyRef = useRef(false);
  const [acceptOfferBusyId, setAcceptOfferBusyId] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/orders?tenant=${encodeURIComponent(selectedTenant.name)}`
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch orders');
        setOrders([]);
        setColumns([]);
      } else {
        setOrders(Array.isArray(data.orders) ? data.orders : []);
        setColumns(Array.isArray(data.columns) ? data.columns : []);
      }
    } catch (e) {
      console.error('Error fetching orders:', e);
      setError('Network error. Please try again.');
      setOrders([]);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchOrders();
    } else {
      setOrders([]);
      setColumns([]);
      setLoading(false);
    }
  }, [selectedTenant, fetchOrders]);

  const categorySelectOptions = useMemo(() => {
    const set = new Set((productDescriptions || []).filter(Boolean));
    set.add(DEFAULT_QUOTE_CATEGORY);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'da'));
  }, [productDescriptions]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (columnField, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [columnField]: value.toLowerCase()
    }));
  };

  const clearAllFilters = () => {
    setSearchFilters({});
  };

  const handleCloseQuoteModal = useCallback(() => {
    setQuoteModalOpen(false);
    setQuoteOrder(null);
    setQuoteTitle('');
    setProfitMargin(DEFAULT_QUOTE_PROFIT_MARGIN);
    setCbcLines([]);
    setLineCategories({});
    setProductDescriptions([]);
    setQuoteModalLoading(false);
    setQuoteModalError(null);
    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);
    setQuoteSubmitting(false);
  }, []);

  const handleCreateQuote = (order) => {
    const zendeskCol = Object.keys(order).find((k) => k.toLowerCase() === 'zendesk-id');
    const zendeskId = zendeskCol ? order[zendeskCol] : '';
    const defaultTitle = `Quote — ${order.subject || zendeskId || `#${order.id}`}`;
    setQuoteTitle(defaultTitle);
    setProfitMargin(DEFAULT_QUOTE_PROFIT_MARGIN);
    setLineCategories({});
    setCbcLines([]);
    setProductDescriptions([]);
    setQuoteModalError(null);
    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);
    setQuoteSubmitting(false);
    setQuoteOrder(order);
    setQuoteModalOpen(true);
  };

  useEffect(() => {
    if (!quoteModalOpen || !quoteOrder || !selectedTenant) return undefined;

    let cancelled = false;

    async function loadQuoteModalData() {
      setQuoteModalLoading(true);
      setQuoteModalError(null);

      try {
        const tenantQ = encodeURIComponent(selectedTenant.name);
        const orderIdEnc = encodeURIComponent(String(quoteOrder.id));
        const descUrl = `${getApiBaseUrl()}/api/economic-products/descriptions`;
        const cbcUrl = `${getApiBaseUrl()}/api/orders/${orderIdEnc}/cbc?tenant=${tenantQ}`;

        const [descRes, cbcRes] = await Promise.all([
          authenticatedFetch(descUrl),
          authenticatedFetch(cbcUrl),
        ]);

        const descJson = await descRes.json().catch(() => ({}));
        const cbcJson = await cbcRes.json().catch(() => ({}));

        if (cancelled) return;

        if (descRes.ok && descJson.success) {
          setProductDescriptions(Array.isArray(descJson.descriptions) ? descJson.descriptions : []);
        } else {
          setProductDescriptions([]);
          console.warn('economic-products/descriptions:', descJson.error || descRes.statusText);
        }

        if (!cbcRes.ok || !cbcJson.success) {
          setCbcLines([]);
          const detail = cbcJson.detail;
          const detailStr =
            typeof detail === 'string' ? detail : detail != null ? JSON.stringify(detail) : '';
          setQuoteModalError(
            [cbcJson.error, detailStr].filter(Boolean).join(' — ') ||
              `Could not load order lines (${cbcRes.status}).`
          );
          return;
        }

        const rawLines = Array.isArray(cbcJson.lines) ? cbcJson.lines : [];
        const lines = rawLines.map((ln) => {
          const pFromApi =
            ln.priceIncVat != null && ln.priceIncVat !== '' && Number.isFinite(Number(ln.priceIncVat))
              ? Number(ln.priceIncVat)
              : ln.unitPrice !== '' && ln.unitPrice != null && Number.isFinite(Number(ln.unitPrice))
                ? Number(ln.unitPrice)
                : null;
          return {
            ...ln,
            priceIncVat: pFromApi,
            isManual: false,
            priceIsOrderTotal: !!ln.priceIsOrderTotal
          };
        });
        setCbcLines(lines);

        const initialCats = {};
        lines.forEach((ln) => {
          if (ln != null && ln.key != null) initialCats[String(ln.key)] = DEFAULT_QUOTE_CATEGORY;
        });
        setLineCategories(initialCats);
      } catch (e) {
        if (!cancelled) {
          console.error('Quote modal load:', e);
          setQuoteModalError('Network error while loading quote data.');
          setCbcLines([]);
        }
      } finally {
        if (!cancelled) setQuoteModalLoading(false);
      }
    }

    loadQuoteModalData();
    return () => {
      cancelled = true;
    };
  }, [quoteModalOpen, quoteOrder, selectedTenant, authenticatedFetch]);

  const handleLineCategoryChange = (lineKey, value) => {
    setLineCategories((prev) => ({ ...prev, [String(lineKey)]: value }));
  };

  const quoteTotals = useMemo(() => {
    const marginPct = profitMargin === '' ? 0 : Number(profitMargin);
    const m = Number.isFinite(marginPct) ? Math.max(0, marginPct) : 0;
    let exVatSum = 0;
    let withMarginSum = 0;
    let marginTooHigh = m >= 100;
    for (const line of cbcLines) {
      const lineEx = lineExVatAmount(line);
      if (lineEx == null || !Number.isFinite(lineEx)) continue;
      exVatSum += lineEx;
      const net = exVatSaleFromMarginOnSalePreview(lineEx, m);
      if (net == null) marginTooHigh = true;
      else withMarginSum += net;
    }
    return { exVatSum, withMarginSum, marginPct: m, marginTooHigh };
  }, [cbcLines, profitMargin]);

  const handleAddQuoteLine = useCallback(() => {
    const newKey = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setCbcLines((prev) => [
      ...prev,
      {
        key: newKey,
        description: '',
        quantity: 1,
        unitPrice: '',
        currency: '',
        priceIncVat: null,
        isManual: true,
        priceIsOrderTotal: false
      }
    ]);
    setLineCategories((prev) => ({ ...prev, [newKey]: DEFAULT_QUOTE_CATEGORY }));
  }, []);

  const handleRemoveQuoteLine = useCallback((key) => {
    setCbcLines((prev) => prev.filter((l) => String(l.key) !== String(key)));
    setLineCategories((prev) => {
      const next = { ...prev };
      delete next[String(key)];
      return next;
    });
  }, []);

  const handleQuoteLinePatch = useCallback((key, patch) => {
    setCbcLines((prev) =>
      prev.map((l) => (String(l.key) === String(key) ? { ...l, ...patch } : l))
    );
  }, []);

  const handlePriceIncVatInput = useCallback((key, valueStr) => {
    if (valueStr === '' || valueStr == null) {
      handleQuoteLinePatch(key, { priceIncVat: null, unitPrice: '' });
      return;
    }
    const n = Number(valueStr);
    if (Number.isFinite(n)) {
      handleQuoteLinePatch(key, { priceIncVat: n, unitPrice: valueStr });
    }
  }, [handleQuoteLinePatch]);

  const handleSubmitQuoteDraft = async (e) => {
    e.preventDefault();
    if (!selectedTenant || !quoteOrder || quoteSubmitting) return;

    setQuoteSubmitError(null);
    setQuoteSubmitSuccess(null);

    const titleTrimmed = quoteTitle.trim();
    if (!titleTrimmed) {
      setQuoteSubmitError('Quote title is required.');
      return;
    }

    if (!cbcLines.length) {
      setQuoteSubmitError('No order lines to submit.');
      return;
    }

    const parsedMargin = profitMargin === '' ? null : Number(profitMargin);
    if (profitMargin !== '' && Number.isNaN(parsedMargin)) {
      setQuoteSubmitError('Profit margin must be a number.');
      return;
    }
    if (parsedMargin !== null && Number.isFinite(parsedMargin) && parsedMargin >= 100) {
      setQuoteSubmitError('Profit margin must be less than 100% (share of selling price).');
      return;
    }

    setQuoteSubmitting(true);
    try {
      const body = {
        tenant: selectedTenant.name,
        ticketId: quoteOrder.id,
        quoteTitle: titleTrimmed,
        profitMargin: parsedMargin,
        lines: cbcLines.map((line) => ({
          key: line.key,
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          currency: line.currency,
          priceIncVat: getLinePriceIncVat(line),
          categoryDescription: lineCategories[String(line.key)] || DEFAULT_QUOTE_CATEGORY,
          isManual: !!line.isManual,
          priceIsOrderTotal: !!line.priceIsOrderTotal
        })),
      };

      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/orders/quote-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setQuoteSubmitError(data.error || (typeof data.detail === 'string' ? data.detail : '') || 'Submit failed.');
        return;
      }

      setQuoteSubmitSuccess(
        data.quoteNumber != null && data.quoteNumber !== ''
          ? `${data.message || 'Quote draft created.'} (e-conomic #${data.quoteNumber})`
          : data.message || 'Submitted.'
      );
      fetchOrders();
    } catch (err) {
      console.error(err);
      setQuoteSubmitError('Network error.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleAcceptOffer = useCallback(
    async (order) => {
      if (!selectedTenant?.name || !order?.id || acceptOfferBusyRef.current) return;

      acceptOfferBusyRef.current = true;
      setAcceptOfferBusyId(order.id);

      try {
        const response = await authenticatedFetch(`${getApiBaseUrl()}/api/orders/accept-offer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant: selectedTenant.name,
            cbcOrderId: String(order.id),
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.success) {
          const detail = data.detail;
          const nested =
            detail && typeof detail === 'object' && detail.message != null
              ? detail.message
              : detail;
          const msg =
            (typeof nested === 'string' ? nested : null) ||
            (typeof nested === 'object' ? JSON.stringify(nested) : null) ||
            (typeof data.error === 'string' ? data.error : null) ||
            'Could not update Zendesk.';
          window.alert(msg);
          return;
        }

        window.alert(
          'Thank you for your order!\n\nWe have received your acceptance and our team has been notified. We will proceed with ordering the equipment.'
        );
        fetchOrders();
      } catch (e) {
        console.error(e);
        window.alert('Network error.');
      } finally {
        acceptOfferBusyRef.current = false;
        setAcceptOfferBusyId(null);
      }
    },
    [selectedTenant, authenticatedFetch, fetchOrders]
  );

  const handleOpenQuotePdf = useCallback(
    async (e, order) => {
      e.preventDefault();
      if (!selectedTenant || !order) return;
      try {
        const params = new URLSearchParams({
          tenant: selectedTenant.name,
          cbcOrderId: String(order.id)
        });
        const response = await authenticatedFetch(
          `${getApiBaseUrl()}/api/orders/quote-pdf?${params.toString()}`
        );
        const ct = response.headers.get('content-type') || '';
        if (!response.ok) {
          let msg = 'Could not open quote PDF.';
          if (ct.includes('application/json')) {
            const data = await response.json().catch(() => ({}));
            msg =
              (typeof data.detail === 'string' ? data.detail : null) ||
              (typeof data.error === 'string' ? data.error : null) ||
              msg;
          }
          window.alert(msg);
          return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (err) {
        console.error(err);
        window.alert('Network error.');
      }
    },
    [selectedTenant, authenticatedFetch]
  );

  const formatValue = (value, column) => {
    if (value === null || value === undefined || value === '') {
      return <span className="null-value">-</span>;
    }

    // Format based on column type
    if (column.type && column.type.includes('int')) {
      return value.toString();
    }
    
    if (column.type && column.type.includes('date')) {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type && column.type.includes('datetime')) {
      return new Date(value).toLocaleString();
    }
    
    return value.toString();
  };

  const getColumnDisplayName = (fieldName) => {
    const displayNames = {
      'id': 'ID',
      'title': 'Title',
      'description': 'Description',
      'status': 'Status',
      'priority': 'Priority',
      'assigned_to': 'Assigned To',
      'created_by': 'Created By',
      'created_at': 'Created At',
      'updated_at': 'Updated At',
      'due_date': 'Due Date',
      'category': 'Category',
      'type': 'Type',
      'subject': 'Subject',
      'zendesk_id': 'Order Number',
      'zendesk-id': 'Order Number'
    };
    
    return displayNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter to only show created_at, subject, and zendesk-id columns
  const filteredColumns = columns.filter(column => {
    const fieldName = column.field.toLowerCase();
    return fieldName === 'created_at' || fieldName === 'subject' || fieldName === 'zendesk-id' || fieldName === 'zendesk_id';
  });

  // Filter and sort orders
  const filteredAndSortedOrders = useMemo(() => {
    let filtered = orders;

    // Apply search filters
    if (Object.keys(searchFilters).length > 0) {
      filtered = orders.filter(order => {
        return Object.entries(searchFilters).every(([columnField, searchValue]) => {
          if (!searchValue) return true;
          const cellValue = order[columnField];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(searchValue);
        });
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        let comparison = 0;
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [orders, searchFilters, sortConfig]);

  if (loading) {
    return (
      <div className="tickets-container">
        <div className="loading-container">
          <Icon name="tickets" size="large" className="loading-icon" />
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tickets-container">
        <div className="error-container">
          <Icon name="tickets" size="large" className="error-icon" />
          <h3>Error Loading Orders</h3>
          <p>{error}</p>
          <button onClick={fetchOrders} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tickets-container">
      <div className="tickets-header">
        <div className="tickets-title-section">
          <h2>Orders</h2>
        </div>
        <div className="tickets-actions">
          {Object.keys(searchFilters).length > 0 && (
            <button onClick={clearAllFilters} className="clear-filters-button">
              Clear Filters
            </button>
          )}
          <span className="tickets-count">
            {filteredAndSortedOrders.length} of {orders.length} order{orders.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="no-tickets-container">
          <Icon name="tickets" size="large" className="no-tickets-icon" />
          <h3>No Orders Found</h3>
          <p>No orders found.</p>
        </div>
      ) : (
        <div className="tickets-grid-container">
          <div className="tickets-grid">
            <table className="tickets-table">
              <thead>
                <tr>
                  {filteredColumns.map((column) => (
                    <th 
                      key={column.field} 
                      className="table-header sortable"
                      onClick={() => handleSort(column.field)}
                    >
                      <div className="header-content">
                        <span>{getColumnDisplayName(column.field)}</span>
                        {sortConfig.key === column.field && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="table-header">Actions</th>
                </tr>
                <tr className="search-row">
                  {filteredColumns.map((column) => (
                    <th key={`search-${column.field}`} className="search-header">
                      <input
                        type="text"
                        placeholder={`Search ${getColumnDisplayName(column.field)}...`}
                        value={searchFilters[column.field] || ''}
                        onChange={(e) => handleSearchChange(column.field, e.target.value)}
                        className="column-search-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                  <th className="search-header"></th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedOrders.map((order, index) => (
                  <tr key={order.id || index} className="table-row">
                    {filteredColumns.map((column) => {
                      const cellValue = order[column.field];
                      const fieldLower = column.field.toLowerCase();
                      if (fieldLower === 'subject' && order.downloadlink) {
                        const text =
                          cellValue != null && cellValue !== ''
                            ? String(cellValue)
                            : formatValue(cellValue, column);
                        return (
                          <td key={column.field} className="table-cell">
                            <button
                              type="button"
                              className="order-subject-download-link"
                              onClick={(e) => handleOpenQuotePdf(e, order)}
                            >
                              {text}
                            </button>
                          </td>
                        );
                      }
                      return (
                        <td key={column.field} className="table-cell">
                          {formatValue(order[column.field], column)}
                        </td>
                      );
                    })}
                    <td className="table-cell">
                      <div className="order-actions">
                        {hasPrivilege('orders', 'create') && (
                          <button
                            className="order-action-button create-quote-button"
                            onClick={() => handleCreateQuote(order)}
                            title="Create Quote"
                          >
                            Create Quote
                          </button>
                        )}
                        {hasPrivilege('orders', 'accept') &&
                          (String(order.quote || '').trim().toLowerCase() === 'accepted' ? (
                            <span
                              className="orders-offer-accepted-label"
                              title="This order was accepted in the portal"
                            >
                              Offer accepted
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="order-action-button accept-offer-button"
                              onClick={() => handleAcceptOffer(order)}
                              disabled={
                                acceptOfferBusyId !== null && acceptOfferBusyId === order.id
                              }
                              title="Accept Offer"
                            >
                              {acceptOfferBusyId === order.id ? 'Submitting…' : 'Accept Offer'}
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {quoteModalOpen && quoteOrder && (
        <div
          className="ticket-form-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseQuoteModal();
          }}
        >
          <div
            className="ticket-form-modal quote-draft-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-draft-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="ticket-form-header">
              <h3 id="quote-draft-title">Create quote</h3>
              <button
                type="button"
                className="ticket-form-close"
                onClick={handleCloseQuoteModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="ticket-form" onSubmit={handleSubmitQuoteDraft}>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.85 }}>
                {quoteOrder.subject || 'Order'}
                <span style={{ opacity: 0.6 }}>
                  {' '}
                  (order #{quoteOrder.id})
                </span>
              </p>

              <div className="ticket-form-field">
                <label htmlFor="quote-draft-title-field">Quote title</label>
                <input
                  id="quote-draft-title-field"
                  type="text"
                  value={quoteTitle}
                  onChange={(e) => setQuoteTitle(e.target.value)}
                  autoComplete="off"
                  disabled={quoteModalLoading}
                />
              </div>

              <div className="ticket-form-field">
                <label htmlFor="quote-profit-margin">Profit margin (%)</label>
                <input
                  id="quote-profit-margin"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Optional"
                  value={profitMargin}
                  onChange={(e) => setProfitMargin(e.target.value)}
                  disabled={quoteModalLoading}
                />
              </div>

              {quoteModalLoading && <p style={{ margin: 0 }}>Loading order lines and categories…</p>}
              {quoteModalError && <div className="ticket-form-error">{quoteModalError}</div>}
              {!quoteModalLoading && !quoteModalError && cbcLines.length === 0 && (
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)' }}>
                  No lines were returned for this order. Use &quot;Add line&quot; to add rows manually.
                </p>
              )}
              {!quoteModalLoading && !productDescriptions.length && cbcLines.length > 0 && (
                <div className="ticket-form-error">
                  No categories found in economic-products (Description column empty or table missing).
                </div>
              )}

              {!quoteModalLoading && !quoteModalError && (
                <div className="ticket-form-field" style={{ gap: '0.75rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}
                  >
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)' }}>
                      Order lines
                    </span>
                    <button
                      type="button"
                      className="quote-draft-add-line"
                      onClick={handleAddQuoteLine}
                      disabled={quoteModalLoading || quoteSubmitting}
                    >
                      + Add line
                    </button>
                  </div>

                  {cbcLines.length > 0 && (
                    <>
                      <div className="quote-draft-lines-scroll">
                        <div className="quote-draft-line-head">
                          <span>Line</span>
                          <span>Price (inc. VAT)</span>
                          <span>Category</span>
                          <span aria-hidden="true" />
                        </div>
                        {cbcLines.map((line, lineIdx) => {
                          const inc = getLinePriceIncVat(line);
                          const lineExVatLine = lineExVatAmount(line);
                          return (
                            <div key={`quote-line-${String(line.key)}-${lineIdx}`} className="quote-draft-line-row">
                              <div>
                                <textarea
                                  className="quote-draft-desc-input quote-draft-desc-textarea"
                                  placeholder="Product description"
                                  rows={2}
                                  value={line.description || ''}
                                  onChange={(e) => handleQuoteLinePatch(line.key, { description: e.target.value })}
                                  disabled={quoteModalLoading || quoteSubmitting}
                                  aria-label={`Product description, line ${lineIdx + 1}`}
                                />
                                <div className="quote-draft-line-meta">
                                  {line.isManual ? (
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                      Qty
                                      <input
                                        type="number"
                                        min="0.001"
                                        step="any"
                                        style={{
                                          width: '4.25rem',
                                          padding: '0.2rem 0.35rem',
                                          borderRadius: 6,
                                          border: '1px solid rgba(255,255,255,0.2)',
                                          background: 'rgba(13, 17, 27, 0.95)',
                                          color: '#f5f7fa',
                                          fontSize: '0.8rem'
                                        }}
                                        value={line.quantity ?? 1}
                                        onChange={(e) => handleQuoteLinePatch(line.key, { quantity: e.target.value })}
                                        disabled={quoteModalLoading || quoteSubmitting}
                                      />
                                    </label>
                                  ) : (
                                    <span>Qty: {line.quantity ?? 1}</span>
                                  )}
                                  {line.currency ? (
                                    <span style={{ marginLeft: '0.5rem' }}>{String(line.currency)}</span>
                                  ) : null}
                                  {lineExVatLine != null ? (
                                    <span style={{ marginLeft: '0.5rem' }}>
                                      · Ex VAT: {formatQuoteAmount(lineExVatLine)}
                                    </span>
                                  ) : (
                                    <span style={{ marginLeft: '0.5rem', opacity: 0.7 }}>· Ex VAT: —</span>
                                  )}
                                </div>
                              </div>
                              <input
                                type="number"
                                className="quote-draft-price-input"
                                min="0"
                                step="0.01"
                                placeholder="—"
                                value={inc != null ? String(inc) : ''}
                                onChange={(e) => handlePriceIncVatInput(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Price including VAT, line ${lineIdx + 1}`}
                              />
                              <select
                                className="quote-draft-category-select"
                                aria-label={`Category for line ${lineIdx + 1}`}
                                value={lineCategories[String(line.key)] ?? DEFAULT_QUOTE_CATEGORY}
                                onChange={(e) => handleLineCategoryChange(line.key, e.target.value)}
                                disabled={quoteModalLoading || quoteSubmitting}
                              >
                                {categorySelectOptions.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="quote-draft-remove-line"
                                onClick={() => handleRemoveQuoteLine(line.key)}
                                disabled={quoteModalLoading || quoteSubmitting}
                                aria-label={`Remove line ${lineIdx + 1}`}
                                title="Remove line"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      <div className="quote-draft-totals">
                        <div className="quote-draft-totals-row muted">
                          <span>Subtotal ex VAT (÷ {VAT_DIVISOR} on price inc. VAT)</span>
                          <strong>{formatQuoteAmount(quoteTotals.exVatSum)}</strong>
                        </div>
                        <div className="quote-draft-totals-row">
                          <span>
                            Total ex VAT (margin % of selling price)
                            {profitMargin !== '' && Number.isFinite(Number(profitMargin))
                              ? ` (${Number(profitMargin)}%)`
                              : ' (0%)'}
                          </span>
                          <strong>
                            {quoteTotals.marginTooHigh
                              ? '—'
                              : formatQuoteAmount(quoteTotals.withMarginSum)}
                          </strong>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {quoteSubmitError && <div className="ticket-form-error">{quoteSubmitError}</div>}
              {quoteSubmitSuccess && <div className="quote-draft-success">{quoteSubmitSuccess}</div>}

              <div className="ticket-form-actions">
                <button type="button" className="secondary" onClick={handleCloseQuoteModal} disabled={quoteSubmitting}>
                  Close
                </button>
                <button
                  type="submit"
                  disabled={
                    quoteModalLoading || quoteSubmitting || !!quoteModalError || cbcLines.length === 0
                  }
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
